import React, { useEffect, useContext, ReactElement } from 'react';
import { Subscription, SubscriptionApiProviderProps as Props, SubscriptionApi } from './constants';
import { SubscriptionsClient } from '../../api/subscriptions/SubscriptionsClient';
import { useCustomerApi } from '../customerApi';
import { signalShopifyCustomerProvidersIsPerksTrue } from '../../../perksInfo/utils';
import { useQueries } from 'react-query';
import { GetCouponCardRecord } from '../../api/subscriptions/types';

export const SubscriptionApiProvider = ({ subscriptionTypes, children }: Props): ReactElement<SubscriptionApi> => {
  const { customer, error: customerError, loading: customerLoading, linkedId } = useCustomerApi();
  const customerId = customer?.id;

  const subscriptionQueries = useQueries(
    subscriptionTypes.map(subscriptionType => ({
      queryKey: ['subscriptionApiProvider', subscriptionType, customerId],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        SubscriptionsClient.getSubscription({ subscriptionType, customerId, signal }),
      enabled: !!customerId && !window.Jbhifi.isPerksDisabled,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }))
  );

  const subscriptions = subscriptionQueries.reduce((acc, query) => {
    if (query?.data?.data?.type) {
      acc[query.data.data.type] = {
        ...query.data.data,
        queryStatus: query.status,
        error: query.error || query.data.error ? query.data.statusCode : undefined,
      };
    }
    return acc;
  }, {} as { [name: string]: Subscription });

  useEffect(() => {
    if (subscriptions?.JBPerks?.isActive) {
      signalShopifyCustomerProvidersIsPerksTrue();
    }
  }, [subscriptions?.JBPerks?.isActive]);

  const refreshSubscriptions = () => {
    subscriptionQueries.forEach(query => query.refetch());
  };

  const getCouponCardAddToWalletLinks = async (couponCode: string): Promise<GetCouponCardRecord> => {
    const { data, error, statusCode } = await SubscriptionsClient.getAddToWalletLinks({ couponCode });
    if (error) {
      console.error('Error fetching add to wallet links for coupon code:', couponCode, error, statusCode);
      return { addToWalletErrorCode: statusCode };
    }
    return {
      appleLink: data?.appleLink,
      googleLink: data?.googleLink,
    };
  };

  return (
    <SubscriptionApi.Provider
      value={{
        subscriptions,
        refreshSubscriptions,
        getCouponCardAddToWalletLinks,
        customer,
        customerLoading,
        customerError,
        linkedId,
      }}
    >
      {children}
    </SubscriptionApi.Provider>
  );
};

export const useSubscriptionApi = (): SubscriptionApi => {
  const context = useContext(SubscriptionApi);
  if (context === undefined || context === null) {
    throw new Error('useSubscriptionApi must be used within a SubscriptionApiProvider');
  }
  return context;
};
